<script lang="ts" setup>
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import { Float } from '@headlessui-float/vue'
import moment from 'moment'
import GICS from 'gics'
import type { JobItem } from '~/models/Job'
import totalEmployee from '~/assets/data/total-employee.json'

const route = useRoute()
const router = useRouter()
const searchConfig = useSearchApiConfig()
const companyConfig = useCompanyApiConfig()
const industryInfo = ref()
const isShareOpen = ref(false)
const isLoading = ref(true)
const companyDetail = ref()
const jobOpenings = ref()
const tabs = ref([
  { label: 'Overview', value: 'overview', path: `/companies/${route.params?.slug}` },
  { label: 'Jobs Vacancy', value: 'jobs-vacancy', path: `/companies/${route.params?.slug}/jobs-vacancy` },
  { label: 'Salaries and Benefits', value: 'salaries-benefits', path: `/companies/${route.params?.slug}/salaries-benefits` },
  { label: 'Videos and Photos', value: 'videos-photos', path: `/companies/${route.params?.slug}/videos-photos` },
])

if (isEmpty(useGetSubdomain()))
  navigateTo('', { redirectCode: 301 })

// INITIAL FETCH
try {
  const [companyData] = await Promise.all([
    useAsyncData('get_company', () => companyConfig(`/job/companies/get-details?slug=${encodeURIComponent(route.params.slug)}`), { server: true }),
  ])

  const rawCompany = companyData.data.value
  if (!isEmpty(rawCompany)) {
    const country = ['us', 'uk', 'sg', 'id', 'my', 'ph', 'in', 'cn', 'hk']
    const countryCode = rawCompany.location?.country.toLowerCase() === 'gb' ? 'uk' : rawCompany.location?.country.toLowerCase()
    if (country.includes(countryCode)) {
      // if countrycode included in our locales
      if (countryCode === useGetSubdomain()) {
        // if countrycode is the same as current subdomain, then show the job
        const rawMedia = ref()
        if (rawCompany?.employerVersion === 2 && (!isEmpty(rawCompany?.videos) || !isEmpty(rawCompany?.photos_all)))
          rawMedia.value = (!isEmpty(rawCompany?.videos) && !isEmpty(rawCompany?.photos_all)) ? rawCompany?.videos.concat(rawCompany?.photos_all) : (!isEmpty(rawCompany?.videos) ? rawCompany?.videos : rawCompany?.photos_all)
        else if (rawCompany?.employerVersion === 1 && !isEmpty(rawCompany?.photos_all))
          rawMedia.value = rawCompany?.photos_all

        const combineMedia = ref([])
        combineMedia.value = !isEmpty(rawMedia.value)
          ? rawMedia.value.map((item: string, idx: number) => {
            const itemId = item.match(/\/embed\/([^?]+)/)
            const itemType = (!isEmpty(rawCompany?.videos) && idx <= (rawCompany?.videos.length - 1)) ? 'video' : 'photo'
            if ((itemId !== null && itemType === 'video') || itemType === 'photo') {
              return {
                id: itemId ? itemId[1] : null,
                url: item,
                type: itemType,
              }
            }
            return false
          })
          : []

        companyDetail.value = { ...rawCompany, previewMedia: combineMedia.value.filter(item => item !== false) }
        industryInfo.value = new GICS(companyDetail.value?.gicsIndustry?.subIndustry) || []

        // GET JOB OPENING
        await Promise.all([
          useAsyncData('get_job_opening', () => searchConfig('/jobs/search', {
            method: 'POST',
            body: {
              country: useGetSubdomain(),
              cities: '',
              types: ['full_time', 'part_time', 'freelance', 'internship'],
              remote: '',
              date_published_from: null,
              date_published_to: null,
              company: route.params.slug,
              term: '',
              from: 0,
              size: 6,
              sort: 'date',
            },
          }), { server: true })]).then((values2) => {
          const rawOpening = values2[0].data.value
          if (!isEmpty(rawOpening)) {
            const resp = rawOpening as { total: number; hits?: JobItem[] }
            jobOpenings.value = resp?.hits
          }
        })
      }
      else {
        // if countrycode different from the subdomain, then redirect it to its own subdomain
        navigateTo(useSetSubdomain(countryCode) + route.path, { external: true, redirectCode: 301 })
      }
    }
    else {
      // if countrycode outside of our locale, show error page
      showError({ statusCode: 404, statusMessage: 'Page Not Found', fatal: true })
    }
  }
  else {
    // if company not exist
    showError({ statusCode: 404, statusMessage: 'Page Not Found', fatal: true })
  }
}
catch (error) {
  console.error('Error fetching data', error)
  showError({ message: 'Failed to load data', statusCode: 500 })
}
finally {
  isLoading.value = false
}

function changeTab(newPath: string) {
  router.push(`${newPath}`)
}
</script>

<template>
  <BaseAtomsSkeletonBounded v-if="!isLoading" class="text-gray-900 !py-0 <md:px-0">
    <div class="flex items-center relative mb-4 mt-6 md:mt-10 px-4 md:px-0">
      <div class="cursor-pointer text-sm" @click="$router.back()">
        <Icon name="heroicons:arrow-left-solid" class="h-5 w-5 mr-2" /> Back
      </div>
      <div class="flex-shrink-0 cursor-pointer ml-auto" @click="isShareOpen = true">
        <Icon name="material-symbols:ios-share" class="w-5 h-5" />
      </div>
      <Popover>
        <Float :offset="15" enter="transition duration-200 ease-out" enter-from="scale-95 opacity-0" enter-to="scale-100 opacity-100" leave="transition duration-150 ease-in" leave-from="scale-100 opacity-100" leave-to="scale-95 opacity-0" tailwindcss-origin-class placement="top" portal arrow>
          <PopoverButton as="span" class="cursor-pointer flex ml-2">
            <Icon name="mdi:dots-vertical" class="w-5 h-5" />
          </PopoverButton>

          <PopoverPanel v-slot="{ close }" class="min-w-[250px] bg-white border border-gray-200 rounded-md shadow-lg focus:outline-none">
            <ul class="py-2 text-sm">
              <li class="py-1 px-3 cursor-pointer">
                <a href="https://tally.so/r/wL7VVy" target="_blank" class="flex items-center">
                  <Icon name="mdi:information" class="w-4 h-4 mr-2 text-indigo-500" />
                  Report
                </a>
              </li>
              <li class="py-1 px-3 cursor-pointer">
                <a href="https://tally.so/r/mVzdKM" target="_blank" class="flex items-center">
                  <Icon name="material-symbols:stars" class="w-4 h-4 mr-2 text-indigo-500" />
                  Claim this company
                </a>
              </li>
            </ul>
          </PopoverPanel>
        </Float>
      </Popover>
    </div>

    <div v-if="!isEmpty(companyDetail)" class="space-y-6 md:space-y-10">
      <!-- Company Card -->
      <div class="px-6 pt-6 shadow-md bg-white rounded-md">
        <div class="flex md:items-center mb-4">
          <div class="w-12 h-12 flex-shrink-0">
            <img v-if="companyDetail?.logo" :src="companyDetail?.logo">
            <div v-else class="bg-cyan-50 text-cyan-500 text-3xl md:text-5xl font-medium text-center flex items-center w-full h-full">
              <span class="m-auto">{{ companyDetail?.name?.charAt(0) }}</span>
            </div>
          </div>
          <div class="ml-4">
            <h2 class="text-2xl font-bold">
              {{ companyDetail?.name }}
            </h2>
            <p v-if="!isEmpty(companyDetail?.founder)" class="mt-1">
              <span class="text-sm text-gray-500">Founded by : </span>
              {{ !isEmpty(companyDetail?.founder?.name) ? companyDetail?.founder?.name.replaceAll(',', ' & ') : '-' }}, {{ moment.unix(companyDetail?.founder?.date / 1000).format("D MMMM YYYY") }}
            </p>
          </div>
        </div>

        <!-- 4 box info -->
        <div class="grid grid-cols-12 gap-x-6 gap-y-6 my-10">
          <div class="col-span-12 md:col-span-6 lg:col-span-4 flex items-center">
            <Icon name="material-symbols:location-on-outline" class="w-6 h-6 mr-4 text-gray-500" />
            {{ !isEmpty(companyDetail?.location?.city) ? `${companyDetail?.location?.city}, ` : (!isEmpty(companyDetail?.location?.state) ? `${companyDetail?.location?.state}, ` : '') }} {{ companyDetail?.location?.countryName }}
          </div>
          <div class="col-span-12 md:col-span-6 lg:col-span-8 flex items-center">
            <Icon name="material-symbols:factory-outline" class="w-6 h-6 mr-4 text-gray-500" />
            <NuxtLink v-if="!isEmpty(companyDetail?.gicsIndustry?.subIndustry) && industryInfo.level(3)" :to="`/search?st=companies&q=&location=all&industry=${industryInfo.level(3)?.name.toLowerCase().replaceAll(' ', '_')}&page=1`" target="_blank" class="text-[#5843E5]">
              {{ industryInfo.level(3).name }}
            </NuxtLink>
            <span v-else>N/A</span>
          </div>
          <div class="col-span-12 md:col-span-6 lg:col-span-4 flex items-center">
            <Icon name="mdi:people-outline" class="w-6 h-6 mr-4 text-gray-500" />
            {{ !isEmpty(companyDetail?.size) ? `${totalEmployee.find(obj => obj.value === companyDetail?.size)?.label} employees` : '-' }}
          </div>
          <div class="col-span-12 md:col-span-6 lg:col-span-8 flex items-center">
            <Icon name="material-symbols:language" class="w-6 h-6 mr-4 text-gray-500" />
            <NuxtLink v-if="!isEmpty(companyDetail.website)" :to="companyDetail.website" target="_blank" class="text-[#5843E5]">
              {{ companyDetail?.website }}
            </NuxtLink>
            <span v-else>N/A</span>
          </div>
        </div>

        <div class="<md:overflow-x-scroll">
          <div class="flex space-x-2" style="width: max-content">
            <button v-for="(item, idx) in tabs" :key="idx" class="px-2 pb-2 pt-2.5 flex items-center text-sm" :class="[route.path === item.path ? 'font-bold border-b-2 border-[#5843E5] focus:ring-0 focus:outline-none text-[#212121]' : 'text-[#666666]']" @click="changeTab(item.path)">
              <div class="mx-auto">
                {{ item?.label }}
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>

    <NuxtPage :company="companyDetail" :jobs="jobOpenings" class="mt-6" />
  </BaseAtomsSkeletonBounded>
</template>
